import {Comparator, moment, Period, Utils} from 'revlock-webutils';
import {updateRatablePlan} from "./plan/ApportionedRatablePlan"
import {Codes} from "./ErrorCode";
import RecognitionRule from './reference/RecognitionRule';
import * as ArrangementUtils from './ArrangementUtils'
import shortId from "shortid";

/**
 * Update to a base PS plan that recognizes the entire amount
 * in the last period and nothing before that. This plan
 * will be subsequently updated when service delivery logs
 * are gathered.
 *
 * @param plan
 * @param productRevenue
 */
export function updateToBaseServicePlan(plan, productRevenue) {
    plan.forEach(p => {
        p.planAmount = 0.0
        p.percentRecognized = 0.0
    })
    if (plan.length > 0) {
        plan[plan.length - 1].percentRecognized = 1.0
        plan[plan.length - 1].planAmount = productRevenue
    }
    //a default plan with no amounts in it for proportional performance
    //recognition as service delivery records are consumed
}

/**
 * Update the plan based on service delivery information that is available
 *
 * @param revenueArrangementItem
 * @param salesOrder
 * @param errors
 * @param enableBetaFeatures
 */
export function updatePSPlan(revenueArrangementItem, salesOrder, errors, enableBetaFeatures, calendarConfig, incrementalPercentComplete) {

    let productRevenue = revenueArrangementItem.productRevenue

    if (salesOrder.revenueArrangement) {
        const currentRevenueArrangement = salesOrder.revenueArrangement.find(ra => ra.id === revenueArrangementItem.revenueArrangementId)

        if (currentRevenueArrangement.isRevenueDelayed) {
            // revenue needs to be delayed
            productRevenue = 0
        }
    }

    updateToBaseServicePlan(revenueArrangementItem.plan, productRevenue)
    //a default plan with no amounts in it for proportional performance
    //recognition as service delivery records are consumed

    if (revenueArrangementItem.recognitionRule.id === RecognitionRule.PERCENT_OF_COMPLETE) {
        updatePSPlanPercentageOfCompletion(revenueArrangementItem, salesOrder, errors, productRevenue, calendarConfig);
    } else if (!enableBetaFeatures) {
        updatePSPlan_default(revenueArrangementItem, salesOrder, errors, productRevenue, calendarConfig);
    } else {
        updatePSPlanProportionalPerformance(revenueArrangementItem, salesOrder, errors, productRevenue, calendarConfig, incrementalPercentComplete);
    }
}

const toPeriod = period => {
    if (!Utils.isValidPeriodFormat(period)) {
        return Utils.formatToAccountingPeriod(period)
    }

    return period;
}

function getCumulativePSDByPeriod(deliveryLog) {
    const groupedByPeriod = Utils.groupByOnArray(deliveryLog, E => toPeriod(E.period));
    const psdLogs = []
    Object.keys(groupedByPeriod).forEach(period => {
        const psdLog = groupedByPeriod[period]
        const psd = {}
        Object.assign(psd, psdLog[0], {unitsDelivered: undefined, percentComplete: undefined})
        psdLog.forEach(log => {

            if (log['unitsDelivered']) {
                if (psd['unitsDelivered'] == undefined) {
                    psd['unitsDelivered'] = 0
                }
                psd['unitsDelivered'] += log['unitsDelivered']

            } else if (log['percentComplete']) {
                if (psd['percentComplete'] == undefined) {
                    psd['percentComplete'] = 0
                }
                psd['percentComplete'] += log['percentComplete']
            }
        })

        psdLogs.push(psd)
    })

    psdLogs.sort(function (log1, log2) {
        return moment(log1.period, Utils.PERIOD_FORMAT).diff(moment(log2.period, Utils.PERIOD_FORMAT))
    })

    return psdLogs
}

export function getDisabledPSDEntries(psdLogs) {
    const toReturn = []

    psdLogs.forEach(log => {
        if (log.disabled != undefined && log.disabled == true) {
            toReturn.push(log)
        }
    })

    toReturn.sort(function (log1, log2) {
        return moment(log1.period, Utils.PERIOD_FORMAT).diff(moment(log2.period, Utils.PERIOD_FORMAT))
    })

    return toReturn
}

export function preparePSDHours(psdLogs) {
    const disabledPSDEntries = getDisabledPSDEntries(psdLogs);
    let totalDisabledHours = 0
    let addDisabledHours = false
    if (disabledPSDEntries.length > 0) {
        const lastDisabledObject = disabledPSDEntries[disabledPSDEntries.length - 1]
        totalDisabledHours = lastDisabledObject.unitsDelivered
        addDisabledHours = true
    }

    let previousPSD = 0
    psdLogs.forEach((currentPsd, index) => {
        if (currentPsd['unitsDelivered'] != undefined && (currentPsd.disabled == undefined || currentPsd.disabled == false)) {
            if (addDisabledHours) {
                previousPSD += totalDisabledHours
                addDisabledHours = false
            }
            currentPsd['unitsDelivered'] += previousPSD
            previousPSD = currentPsd['unitsDelivered']
        }
    })
}

export function getDeliveryLogByPeriod(soiPSDelivery, calendarConfig) {
    let deliveryLogByPeriod = undefined
    if (soiPSDelivery.isLegacy == undefined || soiPSDelivery.isLegacy == true) {
        deliveryLogByPeriod = Utils.arrayToObject(soiPSDelivery.deliveryLog, E => Period.toActgPeriod(E.period, calendarConfig).period);
    } else {
        const psdLogs = getCumulativePSDByPeriod(soiPSDelivery.deliveryLog)

        preparePSDHours(psdLogs)

        deliveryLogByPeriod = Utils.arrayToObject(psdLogs, E => toPeriod(E.period));
    }

    return deliveryLogByPeriod
}

export function updatePSPlan_default(revenueArrangementItem, salesOrder, errors, productRevenue, calendarConfig) {
    let {professionalServiceDelivery} = salesOrder;

    if (professionalServiceDelivery) {
        let {salesOrderItem, plan} = revenueArrangementItem;
        let soiPSDelivery = professionalServiceDelivery.soiProfessionalServiceDelivery.find(psd => {
            let itemId = revenueArrangementItem.salesOrderItem.rootId || revenueArrangementItem.salesOrderItem.id
            if (psd.salesOrderItemId === itemId) return psd
        });

        if (soiPSDelivery) {
            let deliveryLogByPeriod = getDeliveryLogByPeriod(soiPSDelivery, calendarConfig)

            let cumulativePercentRecognized = 0;
            for (let planElement of plan) {
                const period = planElement.actgPeriod?.period || planElement.period;
                if (!period)
                    throw new Error("Bad plan state: missing period")

                if (cumulativePercentRecognized === 1) {
                    // we have recognized everything 100%
                    break;
                }

                const deliveryLog = deliveryLogByPeriod[period];
                let percentRecognized = 0.0;
                if (deliveryLog) {
                    let { unitsDelivered, percentComplete } = deliveryLog;

                    if (percentComplete) {
                        percentComplete = Math.abs(percentComplete)
                    } else {
                        unitsDelivered = Math.abs(unitsDelivered)
                    }


                    if (percentComplete) {
                        percentRecognized = deliveryLog.percentComplete;
                        if (percentRecognized > 1) {
                            percentRecognized = 1
                            if (errors) {
                                errors.add(Codes.TRAN_25, {
                                    salesOrder, salesOrderItem,
                                    'unitsLogPosted': deliveryLog.percentComplete,
                                    'actualUnitsSold': 1,
                                    'period': deliveryLog.period,
                                    'extraUnits': deliveryLog.percentComplete - 1
                                });
                            }
                        }
                    } else {
                        if (unitsDelivered > salesOrderItem.quantity) {
                            if (errors) {
                                errors.add(Codes.TRAN_25, {
                                    salesOrder, salesOrderItem,
                                    'period': deliveryLog.period,
                                    'actualUnitsSold': salesOrderItem.quantity,
                                    'unitsLogPosted': unitsDelivered,
                                    'extraUnits': unitsDelivered - salesOrderItem.quantity,
                                });
                            }
                            unitsDelivered = salesOrderItem.quantity
                        }
                        percentRecognized = unitsDelivered / salesOrderItem.quantity
                    }

                    percentRecognized -= cumulativePercentRecognized;
                }

                if (isNaN(percentRecognized))
                    throw new Error("I am dead");

                planElement.percentRecognized = percentRecognized;

                cumulativePercentRecognized += percentRecognized;
            }

            updateRatablePlan(plan, productRevenue);
        }
    }
}

export function updatePSPlanPercentageOfCompletion(revenueArrangementItem, salesOrder, errors, productRevenue) {
    let { professionalServiceDelivery } = salesOrder;

    if (professionalServiceDelivery) {
        let soiPSDelivery = professionalServiceDelivery.soiProfessionalServiceDelivery.find(psd => {
            let itemId = revenueArrangementItem.salesOrderItem.rootId || revenueArrangementItem.salesOrderItem.id
            if (psd.salesOrderItemId === itemId) return psd
        });

        if (soiPSDelivery) {
            let { salesOrderItem, plan: revenuePlan } = revenueArrangementItem;
            let { estimatedTotalCost, estimatedUnitCost } = salesOrderItem

            let { deliveryLog } = soiPSDelivery

            deliveryLog.forEach(log => {
                log.period = toPeriod(log.period)

                if (log.unitsDelivered) {
                    log.hourlyRate = estimatedUnitCost
                    log.costOfUnitsDelivered = log.unitsDelivered * estimatedUnitCost
                }
            })

            let groupedByPeriod = Utils.agg(deliveryLog, ['period'], ['period'], [], [], ['percentComplete', 'unitsDelivered', 'costIncurred', 'costOfUnitsDelivered']);
            let logsByPeriod = Utils.arrayToObject(groupedByPeriod, E => E.period);

            for (let planElement of revenuePlan) {
                const period = planElement.actgPeriod?.period || planElement.period;
                if (!period)
                    throw new Error("Bad plan state: missing period")

                const periodPSD = logsByPeriod[period]

                if (!periodPSD) {
                    planElement.percentRecognized = 0.0;
                    continue;
                }

                planElement.percentRecognized = (periodPSD.costOfUnitsDelivered + periodPSD.costIncurred) / estimatedTotalCost;
            }

            updateRatablePlan(revenuePlan, productRevenue);
        }
    }
}

export function reversePS(salesOrder, referenceData) {

    let {professionalServiceDelivery} = salesOrder;

    if (professionalServiceDelivery) {
        const {organization} = referenceData

        if (professionalServiceDelivery.soiProfessionalServiceDelivery.length > 0) {

            professionalServiceDelivery.soiProfessionalServiceDelivery.forEach(soiPSDelivery => {
                const {deliveryLog, logMethod} = soiPSDelivery

                if (deliveryLog.length > 0) {
                    deliveryLog.forEach(log => {
                        const _log = Utils.cloneDeep(log)

                        if (!organization.isRestatementMode && _log.effectivePeriod < organization.currentPeriod) {
                            _log.effectivePeriod = organization.currentPeriod
                            _log.period = organization.currentPeriod

                            const openPeriod = organization.currentPeriod
                            let openPeriodStartOfMonth = Utils.startOfMonth(openPeriod, Utils.PERIOD_FORMAT)
                            _log.logDate = Utils.formatDate(openPeriodStartOfMonth)
                        }

                        if (logMethod === 'percentComplete') {
                            _log.percentComplete *= -1
                        } else {
                            _log.unitsDelivered *= -1
                        }

                        soiPSDelivery.deliveryLog.push(_log)
                    })
                }
            })
        }
    }

}

export function updatePSPlanProportionalPerformance(
    revenueArrangementItem,
    salesOrder,
    errors,
    productRevenue,
    calendarConfig,
    incrementalPercentComplete
) {
    let {professionalServiceDelivery} = salesOrder;

    let terminatedProductRevenue = undefined
    let soiPSDelivery = undefined
    let cummulativePercentRecognized = 0

    if (professionalServiceDelivery) {
        soiPSDelivery = professionalServiceDelivery.soiProfessionalServiceDelivery.find(psd => {
            let itemId = revenueArrangementItem.salesOrderItem.rootId || revenueArrangementItem.salesOrderItem.id
            if (psd.salesOrderItemId === itemId) return psd
        });

        if (soiPSDelivery) {
            let {salesOrderItem, plan: revenuePlan} = revenueArrangementItem;
            let {salePrice, extendedSalePrice, quantity} = salesOrderItem

            let {deliveryLog} = soiPSDelivery

            // This revenue item has a termination event associated with it,
            // we discard delivery logs after termination.

            if (revenueArrangementItem.terminationDate) {
                const {terminationDate} = revenueArrangementItem;

                // Discard delivery logs after termination Date
                deliveryLog = deliveryLog.filter(log => terminationDate >= log.logDate)
            }

            let firstPeriod = revenuePlan[0].actgPeriod?.period || revenuePlan[0].period

            if (deliveryLog.length > 0) {
                let percentCompleteMethod = !Utils.isNumber(deliveryLog[0].unitsDelivered) && true || false

                soiPSDelivery.logMethod = percentCompleteMethod ? 'percentComplete' : 'unitsDelivered'

                if (percentCompleteMethod) {
                    let percentToDate = 0

                    deliveryLog = deliveryLog.sort(Comparator.getComparator(
                        ['effectivePeriod', 'logDate', 'timestamp'],
                        [Comparator.forType('period'), Comparator.forType('date'), Comparator.forType('number')]
                    ))

                    deliveryLog.forEach(log => {

                        if (incrementalPercentComplete) {
                            // The percentage is already incremental and no need to - percentToDate
                            log.logPercentComplete = log.percentComplete;
                        } else {
                            // percent complete is the client sent percentage
                            // percentToDate is the cumulative percent complete from the last log
                            log.logPercentComplete = log.percentComplete - percentToDate;
                        }

                        log.billableAmount = extendedSalePrice * log.logPercentComplete

                        percentToDate = log.percentComplete

                        if (!log.effectivePeriod) {
                            if (log.period < firstPeriod) {
                                log.effectivePeriod = firstPeriod
                            } else {
                                log.effectivePeriod = log.period
                            }
                        }
                    })
                } else {
                    deliveryLog.forEach(log => {
                        log.hourlyRate = log.hourlyRate || salePrice;
                        log.billableAmount = log.unitsDelivered * log.hourlyRate;
                        if (!log.effectivePeriod) {
                            if (log.period < firstPeriod) {
                                log.effectivePeriod = firstPeriod
                            } else {
                                log.effectivePeriod = log.period
                            }
                        }
                    })
                }

                let groupedByPeriod = Utils.agg(deliveryLog, ['effectivePeriod'], ['effectivePeriod'], [], [], ['billableAmount', 'logPercentComplete', 'unitsDelivered']);
                let logsByPeriod = Utils.arrayToObject(groupedByPeriod, E => E.effectivePeriod);

                let totalUnitsReconisedTillDate = 0
                let totalPercentRecognisedTillDate = 0
                for (let planElement of revenuePlan) {
                    const period = planElement.actgPeriod?.period || planElement.period;
                    if (!period)
                        throw new Error("Bad plan state: missing period")

                    let periodPSD = logsByPeriod[period]
                    if (!periodPSD) {
                        planElement.percentRecognized = 0
                        continue
                    }

                    if (soiPSDelivery.logMethod === 'unitsDelivered') {
                        // if we have already recognised full no need to look in to future what happens.
                        if (totalUnitsReconisedTillDate >= quantity) {
                            planElement.percentRecognized = 0
                        } else {
                            planElement.percentRecognized = (periodPSD.unitsDelivered) / quantity;
                        }
                        totalUnitsReconisedTillDate += periodPSD.unitsDelivered
                    } else {
                        // if we have already recognised full no need to look in to future what happens.
                        if (totalPercentRecognisedTillDate >= 1) {
                            planElement.percentRecognized = 0
                        } else {
                            planElement.percentRecognized = periodPSD.logPercentComplete
                        }
                        totalPercentRecognisedTillDate += periodPSD.logPercentComplete
                    }

                    cummulativePercentRecognized += planElement.percentRecognized
                }

                if (revenueArrangementItem.terminationDate) {
                    // This item is terminated at this point.
                    // Amount that is recognized till termination date is considered to be 100% recognized.
                    revenuePlan.forEach(element => {
                        // For e.g
                        // 202209 = 0.4 (40%) revenue and 202210 = 0.2 (20%) revenue, total revenue is 0.6 (60%)
                        // Changing to
                        // 202209 = 0.4 / 0.6 = 0.666
                        // 202210 = 0.2 / 0.6 = 0.333
                        element.percentRecognized = element.percentRecognized / cummulativePercentRecognized
                    })

                    // Would only be the case when new arrangement is created.
                    if (!revenueArrangementItem.terminatedProductRevenue) {
                        revenueArrangementItem.terminatedProductRevenue =
                            revenueArrangementItem.productRevenue * Math.min(cummulativePercentRecognized, 1)
                    }

                    // here product revenue is passed explictly to this function, so we infer terminatedProductRevenue
                    terminatedProductRevenue = productRevenue * Math.min(cummulativePercentRecognized, 1)

                    // When service log is received in an open period after termination period is closed.
                    // terminated revenue may increase or decrease.
                    if (terminatedProductRevenue) {
                        revenueArrangementItem.terminatedProductRevenue = terminatedProductRevenue
                    }
                }

                if (revenueArrangementItem.terminationDate) {
                    updateRatablePlan(revenuePlan, terminatedProductRevenue);
                } else {
                    updateRatablePlan(revenuePlan, productRevenue);
                }
            }
        }
    }

    // Termination date is present, but NO service is delivered,
    // We zero out the revenue plan in this case.
    if (!soiPSDelivery && revenueArrangementItem.terminationDate) {
        const zeroElement = {
            planAmount: 0,
            percentRecognized: 0,
            cummulativeTotal: 0,
            cummulativePercentRecognized: 0
        }
        revenueArrangementItem.plan.forEach(element => {
            Object.assign(element, zeroElement)
        })
    }

    return {
        cummulativePercentRecognized
    }

}


export const buildServiceDeliveryRollForward = (jobId, salesOrder, org, calendarConfig, incrementalPercentConfig, isLegacy) => {
    salesOrder.revenueArrangement.sort((ra1, ra2) => ra2.effectiveDate < ra1.effectiveDate ? 1 : -1);

    //order arrangements by effective date
    function deliveryLog(salesOrderItem, purchases) {
        const {incrementalPurchases} = purchases
        const percentageToUnitsLog = {}
        const log = {}
        const d =
            salesOrder.professionalServiceDelivery?.soiProfessionalServiceDelivery?.find(soiPS => soiPS.salesOrderItemId === salesOrderItem.id)

        if (!d)
            return {log, percentageToUnitsLog};


        let {deliveryLog, logMethod} = d;
        let priorPeriod
        const day1Log = deliveryLog[0]
        if (day1Log) {
            const logPeriod = day1Log.effectivePeriod || day1Log.period
            const _soItemStartActgPeriod = Period.toActgPeriod(salesOrderItem.startDate, calendarConfig)
            if (logPeriod > _soItemStartActgPeriod.period) {
                // I have gap in the deliveryLogs
                const gapPeriods = Period.periodsBetween(_soItemStartActgPeriod.period, logPeriod, true, false)

                for (const gp of gapPeriods) {
                    const logToPopulate = Utils.cloneDeep(day1Log)

                    const periodStartDate = Period.toStartOfMonth(gp)

                    logToPopulate.effectivePeriod = gp
                    logToPopulate.period = gp
                    logToPopulate.logDate = periodStartDate
                    logToPopulate.percentComplete = 0.0
                    logToPopulate.logPercentComplete = 0.0
                    logToPopulate.billableAmount = 0
                    logToPopulate.unitsDelivered = 0
                    logToPopulate.id = shortId.generate()
                    logToPopulate.timestamp = new Date(periodStartDate).getTime() / 1000

                    deliveryLog.push(logToPopulate)

                }

                deliveryLog = deliveryLog.sort(Comparator.getComparator(
                    ['effectivePeriod', 'logDate', 'timestamp'],
                    [Comparator.forType('period'), Comparator.forType('date'), Comparator.forType('number')]
                ))
            }

        }

        let currentQuantity = 0.0
        // let quantityTillLastPeriod = 0.0
        // let index = 0
        for (let d of deliveryLog) {
            const period = d.effectivePeriod || d.period
            if (!log[period])
                log[period] = 0.0

            if (logMethod == 'percentComplete') {

                if (incrementalPurchases[period] && period != priorPeriod) {
                    currentQuantity += incrementalPurchases[period]
                    priorPeriod = period
                }

                // always converting percetage to units
                if (incrementalPercentConfig) {
                    log[period] += Number(d.logPercentComplete)
                } else {
                    log[period] = Number(d.logPercentComplete)
                }

                percentageToUnitsLog[period] = log[period] * currentQuantity

            } else {
                log[period] += Number(d.unitsDelivered)
            }
        }

        return {log, percentageToUnitsLog}
    }

    function getLogMethod(salesOrderItem) {
        const d =
            salesOrder.professionalServiceDelivery?.soiProfessionalServiceDelivery?.find(soiPS => soiPS.salesOrderItemId === salesOrderItem.id)


        if (d) {
            return d.logMethod
        }
    }

    function purchaseLog(serviceItem, logMethod) {

        let incrementalPurchases = {}
        const incrementalPurchasesByPercentage = {}
        // if this is modification of an order, we will have incremental purchases available at the so service item level
        const soServiceItem = salesOrder.professionalServiceDelivery?.soiProfessionalServiceDelivery?.find(soiPS => soiPS.salesOrderItemId === serviceItem.salesOrderItemId)
        if (soServiceItem && soServiceItem.incrementalPurchases) {
            incrementalPurchases = soServiceItem.incrementalPurchases
        } else {
            // we don't have incrementalPurchases available at the service line item.
            let cumulativeQuantity = 0;
            for (let arrangement of salesOrder.revenueArrangement) {
                const effectiveActgPeriod =
                    Period.toActgPeriod(arrangement.effectiveDate, calendarConfig)
                const item =
                    arrangement.revenueArrangementItem?.find(a => a.salesOrderItemId === serviceItem.salesOrderItemId);
                if (item) {
                    incrementalPurchases[effectiveActgPeriod.period] =
                        item.salesOrderItem.quantity - cumulativeQuantity;
                    cumulativeQuantity = item.salesOrderItem.quantity;
                }
            }
        }


        let priorClosedPeriods = {};
        if(soServiceItem && soServiceItem.priorClosedPeriods) {
            priorClosedPeriods = soServiceItem.priorClosedPeriods;
        }

        if (logMethod == 'percentComplete') {
            // for percentage complete, we need quantity in percentage
            // this way we can calculate the correct numbers
            let priorCumulativeQuantity = 0
            Object.keys(incrementalPurchases).forEach((p, i) => {

                if (i > 0) {
                    incrementalPurchasesByPercentage[p] = incrementalPurchases[p] / (priorCumulativeQuantity + incrementalPurchases[p])
                } else {
                    // on day1 it's always 100%
                    incrementalPurchasesByPercentage[p] = 1
                }

                priorCumulativeQuantity += incrementalPurchases[p]

            })

            // for percentage, I need quantity as well as percentages of quantities
            return {incrementalPurchasesByPercentage, incrementalPurchases, priorClosedPeriods}
        }

        // for units delivered, I only need quantity
        return {
            incrementalPurchasesByPercentage,
            incrementalPurchases,
            priorClosedPeriods
        };
    }

    const currentRevenueArrangement = salesOrder.currentRevenueArrangement

    const rollForward = [];
    const serviceItems =
        currentRevenueArrangement?.revenueArrangementItem
            .filter(item => (item.recognitionRule.id === RecognitionRule.PROPORTIONAL_PERFORMANCE
                || item.recognitionRule.id === RecognitionRule.PERCENT_OF_COMPLETE)) || []

    for (let serviceItem of serviceItems) {
        const {plan, salesOrderItem} = serviceItem
        const logMethod = getLogMethod(salesOrderItem)

        const purchases = purchaseLog(serviceItem, logMethod)
        const {log, percentageToUnitsLog} = deliveryLog(salesOrderItem, purchases);

        const endPeriod = plan[plan.length - 1].actgPeriod.period

        const servicePeriods = plan.map(p => p.actgPeriod.period)

        const deliveryMethod = logMethod == 'unitsDelivered' ? 'Units Delivered' : 'Percentage Delivered'

        if (!isLegacy) {
            if (Object.keys(log).length == 0) {
                buildRollForward(org, salesOrder, salesOrderItem, log, servicePeriods, purchases.incrementalPurchases, endPeriod, rollForward, 'Units Delivered', purchases.priorClosedPeriods, jobId)
                continue
            }

            if (logMethod == 'unitsDelivered') {
                buildRollForward(org, salesOrder, salesOrderItem, log, servicePeriods, purchases.incrementalPurchases, endPeriod, rollForward, deliveryMethod, purchases.priorClosedPeriods, jobId)
            } else {
                buildRollForward(org, salesOrder, salesOrderItem, percentageToUnitsLog, servicePeriods, purchases.incrementalPurchases, endPeriod, rollForward, deliveryMethod, purchases.priorClosedPeriods, jobId)
            }
        } else {
            // legacy client
            let beg = 0.0;
            let end = 0.0;
            let consumed = 0.0;

            for (let period of servicePeriods) {
                beg = end
                consumed = log[period] || 0.0
                const adds = purchases.incrementalPurchases[period] ? purchases.incrementalPurchases[period] : 0.0
                const expired = (period === endPeriod) ? end : 0.0
                end = beg + adds - expired - consumed


                rollForward.push({
                    'OrgId': org.id,
                    'Active_Link_Id': 0,
                    'Customer Id': salesOrder.customerId,
                    'Sales Order Id': salesOrder.id,
                    'Sales Order Item Id': salesOrderItem.rootId || salesOrderItem.id,
                    'Product Id': salesOrderItem.product.id,
                    'Actg Period': period,
                    'Beginning Units': beg,
                    'Consumed Units': consumed,
                    'Overage Units': 0.0,
                    'New Units': adds,
                    'Ending Units': end,
                    'Expired Units': expired,
                    'Beginning Percentage': 0.00,
                    'New Percentage': 0.00,
                    'Consumed Percentage': 0.00,
                    'Overage Percentage': 0.00,
                    'Expired Percentage': 0.00,
                    'Ending Percentage': 0.00,
                    'Cumulative Consumed Percentage': 0.0,
                    'Cumulative Overage Percentage': 0.0,
                    'Log Method': deliveryMethod,
                    'Job Id': jobId,
                    'LastModifiedDate': new Date().toISOString().slice(0, 19).replace('T', ' '),
                });
            }
        }
    }

    return rollForward
}

function buildRollForward(org, salesOrder, salesOrderItem, log, servicePeriods, purchases, endPeriod, rollForward, deliveryMethod, priorClosedPeriods, jobId) {
    let beg = 0.0;
    let end = 0.0;
    let totalQuantityTillPeriod = 0.0
    let cumulativeOverageUnits = 0.0
    let totalComulativeUnitsConsumedUntilLastPeriod = 0.0

    let totalCumulativeUnitsDelivered = 0.0
    let totalCumulativeUnitsDeliveredTillLastPeriod = 0.0
    let priorQuantity = 0.0
    let priorExpired = 0.0;
    let totalCumulativePriorExpired = 0.0;

    let totalComulativePercentConsumed = 0.0
    let totalCumulativeOverage = 0.0

    let index = 0
    for (let period of servicePeriods) {
        beg = end

        let consumed = 0.0;
        let deliveredUnits = log[period] || 0.0
        totalCumulativeUnitsDelivered += deliveredUnits

        let adds = purchases[period] ? purchases[period] : 0.0
        totalQuantityTillPeriod += adds

        let overageUnits = 0.0
        let expired = 0.0

        if (index > 0 && adds > 0) {

            // I had somethind that was overage, but now it seems
            // i can consume it. how much is what we are going to determine
            let unitsToConsumeThisPeriod = totalCumulativeUnitsDeliveredTillLastPeriod - totalComulativeUnitsConsumedUntilLastPeriod
            unitsToConsumeThisPeriod += deliveredUnits

            // 1.0 + 0.166 - 0.9) = 0.2666
            const capacityToConsumeThisPeriod = (totalQuantityTillPeriod - totalComulativeUnitsConsumedUntilLastPeriod)

            if (capacityToConsumeThisPeriod >= unitsToConsumeThisPeriod) {
                consumed = unitsToConsumeThisPeriod
            } else {
                // 0.25 capacityToConsumeThisPeriod
                // 0.3 percToConsumeThisPeriod

                // 0.2 overage + 0.1 currentLog

                consumed = capacityToConsumeThisPeriod
            }

            overageUnits = deliveredUnits - consumed
        } else {
            if (totalCumulativeUnitsDelivered <= totalQuantityTillPeriod) {
                // whatever is delivered is consumed
                consumed = deliveredUnits
            } else {
                // Can I consume something today?
                if (index > 0 && adds < 0) {
                    // quantity has shrinked
                    // Can I consume something today?
                    consumed = deliveredUnits - (totalCumulativeUnitsDelivered - totalQuantityTillPeriod - cumulativeOverageUnits)

                    overageUnits = deliveredUnits

                } else {
                    if (deliveredUnits) {
                        overageUnits = totalCumulativeUnitsDelivered - totalQuantityTillPeriod - cumulativeOverageUnits
                    }
                    consumed = deliveredUnits - overageUnits
                }
            }
        }

        cumulativeOverageUnits += overageUnits
        totalComulativeUnitsConsumedUntilLastPeriod += consumed
        totalCumulativeUnitsDeliveredTillLastPeriod += deliveredUnits


        if (priorExpired) {
            end = totalQuantityTillPeriod - totalComulativeUnitsConsumedUntilLastPeriod
            expired = beg + adds - end - consumed
        }
        else {
            end = beg + adds - Math.abs(expired) - consumed
        }

        if (period == endPeriod) {
            // we have expired units now
            // consumed this period and everything until last
            end = 0
            expired = beg + adds - end - consumed
        }


        if (priorClosedPeriods && (period == priorClosedPeriods[period] && period != endPeriod)) {
            expired = end;
            end = 0;
            priorExpired = expired;
        }

        let newPercentage = adds / totalQuantityTillPeriod
        const begPec = beg / priorQuantity
        const addPerc = adds / totalQuantityTillPeriod

        if (index > 0 && adds && beg) {
            newPercentage = addPerc + ((-1 * begPec) + (begPec * priorQuantity) / totalQuantityTillPeriod)
        }

        let consumedPercentage = consumed / totalQuantityTillPeriod
        let overagePercentage = overageUnits / totalQuantityTillPeriod
        let expiredPercentage = expired / totalQuantityTillPeriod
        let endPercentage = end / totalQuantityTillPeriod

        if (consumedPercentage) {
            totalComulativePercentConsumed += consumedPercentage
        }

        if (overagePercentage) {
            totalCumulativeOverage += overagePercentage
        }

        priorQuantity = totalQuantityTillPeriod

        const toPush = {}
        toPush['OrgId'] = org.id
        toPush['Active_Link_Id'] = 0
        toPush['Customer Id'] = salesOrder.customerId
        toPush['Sales Order Id'] = salesOrder.id
        toPush['Sales Order Item Id'] = salesOrderItem.rootId || salesOrderItem.id
        toPush['Product Id'] = salesOrderItem.product.id
        toPush['Actg Period'] = period
        toPush['Beginning Units'] = beg
        toPush['Consumed Units'] = consumed
        toPush['New Units'] = adds
        toPush['Ending Units'] = end
        toPush['Overage Units'] = overageUnits
        toPush['Expired Units'] = expired
        toPush['Beginning Percentage'] = begPec
        toPush['New Percentage'] = newPercentage
        toPush['Consumed Percentage'] = consumedPercentage
        toPush['Overage Percentage'] = overagePercentage
        toPush['Expired Percentage'] = expiredPercentage
        toPush['Ending Percentage'] = endPercentage
        toPush['Cumulative Consumed Percentage'] = deliveredUnits ? totalComulativePercentConsumed : 0.0
        toPush['Cumulative Overage Percentage'] = deliveredUnits ? totalCumulativeOverage : 0.0
        toPush['Log Method'] = deliveryMethod
        toPush['Job Id'] = jobId
        toPush['LastModifiedDate'] = new Date().toISOString().slice(0, 19).replace('T', ' ')

        rollForward.push(toPush);

        index++
    }
}
